import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react';

export type ThemeMode = 'light' | 'dark' | 'system';
export type Effective = 'light' | 'dark';

interface ThemeCtx {
  mode: ThemeMode;
  effective: Effective;
  setMode: (m: ThemeMode) => void;
  toggle: () => void; // cicla light → dark → system
}

const Ctx = createContext<ThemeCtx | null>(null);
const STORAGE_KEY = 'iasolicitar:theme';

function resolveEffective(mode: ThemeMode): Effective {
  if (mode === 'system') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return mode;
}

function readInitialMode(): ThemeMode {
  try {
    const saved = localStorage.getItem(STORAGE_KEY) as ThemeMode | null;
    if (saved === 'light' || saved === 'dark' || saved === 'system') return saved;
  } catch {}
  return 'system';
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>(readInitialMode);
  const [effective, setEffective] = useState<Effective>(() => resolveEffective(readInitialMode()));

  const apply = useCallback((eff: Effective) => {
    document.documentElement.classList.toggle('dark', eff === 'dark');
    document.documentElement.style.colorScheme = eff;
    setEffective(eff);
  }, []);

  const setMode = useCallback(
    (m: ThemeMode) => {
      try { localStorage.setItem(STORAGE_KEY, m); } catch {}
      setModeState(m);
      apply(resolveEffective(m));
    },
    [apply]
  );

  const toggle = useCallback(() => {
    setMode(mode === 'light' ? 'dark' : mode === 'dark' ? 'system' : 'light');
  }, [mode, setMode]);

  // Ouve mudança do SO quando em "system"
  useEffect(() => {
    if (mode !== 'system') return;
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => apply(mql.matches ? 'dark' : 'light');
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [mode, apply]);

  return (
    <Ctx.Provider value={{ mode, effective, setMode, toggle }}>
      {children}
    </Ctx.Provider>
  );
}

export function useTheme(): ThemeCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useTheme must be used within <ThemeProvider>');
  return ctx;
}
